import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { STATUSES, STATUS_META, ENTITY_META, ENTITY_TYPES } from "@/lib/constants";
import { Phone, Pencil, Trash2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";

function MemberCard({ member, index, onEdit, onDelete, onOpenDetail, canWrite = true }) {
  const meta = STATUS_META[member.status] || STATUS_META.Beyaz;
  const em = ENTITY_META[member.entity_type] || ENTITY_META.Firma;
  const phoneLines = (member.phone || "").split("\n").filter(Boolean);
  const firstPhone = phoneLines[0]?.split(" - ")[0]?.replace(/[^\d+]/g, "");
  return (
    <Draggable draggableId={member.id} index={index} isDragDisabled={!canWrite}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          data-testid={`kanban-card-${member.id}`}
          onClick={() => onOpenDetail?.(member)}
          className={`bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-3 mb-2 border-l-4 transition-shadow ${
            snapshot.isDragging ? "shadow-lg scale-[1.02]" : "shadow-sm hover:shadow-md cursor-pointer"
          }`}
          // Sol şerit rengi satır içi stil: aynı öğede iki border-* rengi
          // olunca Tailwind'in CSS sırası nötr gri rengi kazandırıyordu.
          style={{
            ...provided.draggableProps.style,
            borderLeftColor: member.status === "Beyaz" ? "#D4D4D4" : meta.dot,
          }}
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1 mb-1">
              {member.order_no != null && (
                <span className="inline-flex items-center gap-0.5 text-[9px] font-mono font-bold uppercase bg-neutral-950 dark:bg-neutral-100 text-white dark:text-neutral-950 px-1.5 py-0.5 rounded">
                  #{member.order_no}
                </span>
              )}
              {member.member_no && (
                <span className="text-[9px] font-mono text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-1 py-0.5 rounded">
                  Üye {member.member_no}
                </span>
              )}
              {member.trade_reg_no && (
                <span className="text-[9px] font-mono text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-1 py-0.5 rounded">
                  Tic {member.trade_reg_no}
                </span>
              )}
              {!meta.hideEntitySplit && (
                <span
                  className={`text-[9px] font-semibold uppercase tracking-wider ${em.bg} ${em.text} px-1.5 py-0.5 rounded`}
                  title={`${em.long} — ${em.vote}`}
                >
                  {em.short}
                </span>
              )}
            </div>
            <div className="font-heading font-semibold text-[13px] leading-tight line-clamp-2 text-neutral-950 dark:text-neutral-50">
              {member.name}
            </div>
            {member.company && member.company !== member.name && (
              <div className="mt-1 flex items-center gap-1.5 text-[11px] text-neutral-600 dark:text-neutral-400 min-w-0">
                <Building2 className="h-3 w-3 shrink-0" />
                <span className="truncate">{member.company}</span>
              </div>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-neutral-100 dark:border-neutral-800 pt-2">
            {firstPhone ? (
              <a
                href={`tel:${firstPhone}`}
                className="flex items-center gap-1.5 text-[11px] text-neutral-700 dark:text-neutral-300 hover:text-neutral-950 dark:hover:text-white transition-colors min-w-0"
                data-testid={`kanban-card-phone-${member.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Phone className="h-3 w-3 shrink-0" />
                <span className="font-mono truncate">{phoneLines[0]}</span>
              </a>
            ) : (
              <span className="text-[11px] text-neutral-400">—</span>
            )}
            <div className="flex items-center gap-0.5 shrink-0">
              {canWrite && (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={(e) => { e.stopPropagation(); onEdit(member); }}
                    data-testid={`kanban-edit-${member.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                    onClick={(e) => { e.stopPropagation(); onDelete(member); }}
                    data-testid={`kanban-delete-${member.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default function KanbanBoard({ members, onDragEnd, onEdit, onDelete, onOpenDetail, canWrite = true }) {
  // Her sütunun kendi şirket/şahıs filtresi var: null = hepsi
  const [entityFilters, setEntityFilters] = React.useState({});

  const grouped = STATUSES.reduce((acc, s) => {
    acc[s] = members.filter((m) => m.status === s);
    return acc;
  }, {});

  const toggleFilter = (status, tur) =>
    setEntityFilters((onceki) => ({
      ...onceki,
      [status]: onceki[status] === tur ? null : tur,
    }));

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-4 kanban-scroll px-1 -mx-1"
        data-testid="kanban-board"
      >
        {STATUSES.map((status) => {
          const meta = STATUS_META[status];
          const tumu = grouped[status];
          const filtre = entityFilters[status] || null;
          const list = filtre ? tumu.filter((m) => (m.entity_type || "Firma") === filtre) : tumu;
          return (
            <Droppable droppableId={status} key={status} isDropDisabled={!canWrite}>
              {(provided, snapshot) => (
                <div
                  className={`shrink-0 snap-center w-[85vw] sm:w-72 rounded-lg border ${meta.border} ${meta.bg} flex flex-col ${
                    snapshot.isDraggingOver ? "ring-2 ring-offset-2 dark:ring-offset-neutral-950 " + meta.ring : ""
                  }`}
                  data-testid={`kanban-column-${meta.key}`}
                >
                  <div className="px-3 py-2.5 border-b border-neutral-200/60 dark:border-neutral-700/60 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`h-2 w-2 rounded-full shrink-0 ${meta.indicator}`} />
                      <div className="min-w-0">
                        <h3 className={`font-heading font-semibold text-sm ${meta.text} leading-tight line-clamp-2`}>
                          {meta.label}
                        </h3>
                        <div className={`text-[10px] ${meta.text} opacity-70`}>{meta.desc}</div>
                      </div>
                    </div>
                    <span className={`text-xs font-mono font-semibold ${meta.text}`}>
                      {filtre ? `${list.length}/${tumu.length}` : tumu.length}
                    </span>
                  </div>

                  {/* Oy şekline göre filtre: şirket / şahıs / hepsi. */}
                  {!meta.hideEntitySplit && (
                  <div className="px-3 pb-2.5 flex items-center gap-1.5">
                    {ENTITY_TYPES.map((t) => {
                      const em = ENTITY_META[t];
                      const aktif = filtre === t;
                      const adet = tumu.filter((m) => (m.entity_type || "Firma") === t).length;
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => toggleFilter(status, t)}
                          title={`${em.long} — ${em.vote}`}
                          aria-pressed={aktif}
                          className={`flex-1 inline-flex items-center justify-center gap-1 rounded-md border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                            aktif
                              ? "border-transparent text-white"
                              : "border-neutral-300/70 dark:border-neutral-600/70 text-neutral-600 dark:text-neutral-300 hover:border-neutral-400 dark:hover:border-neutral-500"
                          }`}
                          style={aktif ? { background: em.dot } : undefined}
                          data-testid={`kanban-filter-${meta.key}-${em.key}`}
                        >
                          {!aktif && (
                            <span
                              className="h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: em.dot }}
                            />
                          )}
                          {em.short}
                          <span className="font-mono opacity-70">{adet}</span>
                        </button>
                      );
                    })}
                  </div>
                  )}
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="flex-1 p-2 min-h-[300px] max-h-[calc(100vh-260px)] overflow-y-auto column-scroll"
                  >
                    {list.length === 0 && !snapshot.isDraggingOver && (
                      <div className="text-center text-xs text-neutral-400 dark:text-neutral-500 py-6 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-md">
                        {filtre
                          ? `Bu kolonda ${ENTITY_META[filtre].short.toLowerCase()} ile oy kullanacak üye yok`
                          : "Bu kolonda üye yok"}
                      </div>
                    )}
                    {list.map((m, i) => (
                      <MemberCard key={m.id} member={m} index={i} onEdit={onEdit} onDelete={onDelete} onOpenDetail={onOpenDetail} canWrite={canWrite} />
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
}
